import React, { useState, useEffect, useMemo, useRef } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import {
  successAlert,
  errorAlert,
  warningAlert,
  confirmAlert,
} from "../../utils/alert";
import API from "../../api/api";
import { apiUrl, resolveAssetUrl } from "../../api/config";

const toDateKey = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown-date";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const isSameLocalDate = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatDateHeading = (dateKey) => {
  if (dateKey === "unknown-date") return "Unknown date";

  const [year, month, day] = dateKey.split("-").map(Number);
  const sectionDate = new Date(year, month - 1, day);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameLocalDate(sectionDate, today)) return "Today";
  if (isSameLocalDate(sectionDate, yesterday)) return "Yesterday";

  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(sectionDate);
};

const formatDateSubheading = (dateKey) => {
  if (dateKey === "unknown-date") return "";
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
};

const isExternalUrl = (value = "") => /^https?:\/\//i.test(String(value || "").trim());
const isDirectVideoFile = (url = "") =>
  /\.(mp4|webm|mov|avi|m4v|ogv|ogg)(?:[?#].*)?$/i.test(String(url || "").trim());

const extractYouTubeId = (rawUrl = "") => {
  try {
    const url = new URL(rawUrl);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();
    if (host === "youtu.be") {
      return url.pathname.split("/").filter(Boolean)[0] || "";
    }
    if (host.includes("youtube.com")) {
      if (url.pathname === "/watch") return url.searchParams.get("v") || "";
      const parts = url.pathname.split("/").filter(Boolean);
      if (["embed", "shorts", "live"].includes(parts[0])) return parts[1] || "";
    }
  } catch (_err) {
    return "";
  }
  return "";
};

const toYouTubeEmbedUrl = (rawUrl = "") => {
  const id = extractYouTubeId(rawUrl);
  return id ? `https://www.youtube.com/embed/${id}` : "";
};

const toVimeoEmbedUrl = (rawUrl = "") => {
  try {
    const url = new URL(rawUrl);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();
    if (!host.includes("vimeo.com")) return "";

    const parts = url.pathname.split("/").filter(Boolean);
    if (!parts.length) return "";

    const id =
      parts[0] === "video"
        ? parts[1]
        : parts[0];
    return /^\d+$/.test(id || "") ? `https://player.vimeo.com/video/${id}` : "";
  } catch (_err) {
    return "";
  }
};

const toEmbedUrl = (rawUrl = "") => toYouTubeEmbedUrl(rawUrl) || toVimeoEmbedUrl(rawUrl);

const hostLabel = (rawUrl = "") => {
  try {
    return new URL(rawUrl).hostname.replace(/^www\./i, "");
  } catch (_err) {
    return "External link";
  }
};

const normalizeCategory = (value) => String(value || "").trim().toLowerCase();
const categoryLabel = (category, fileType) => {
  if (normalizeCategory(category) === "player_videos") return "Player Video";
  if (String(fileType || "").toLowerCase() === "video") return "Video";
  return "Photo";
};

const categoryBadgeClass = (category, fileType) => {
  if (normalizeCategory(category) === "player_videos") return "bg-fuchsia-100 text-fuchsia-700";
  if (String(fileType || "").toLowerCase() === "video") return "bg-blue-100 text-blue-700";
  return "bg-emerald-100 text-emerald-700";
};

const ManageGallery = () => {
  const [gallery, setGallery] = useState({});
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0 });
  const [flatGallery, setFlatGallery] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("general");
  const [uploadTitle, setUploadTitle] = useState("");
  const [videoLinkTitle, setVideoLinkTitle] = useState("");
  const [videoLinkUrl, setVideoLinkUrl] = useState("");
  const [savingVideoLink, setSavingVideoLink] = useState(false);
  const sectionRefs = useRef({});

  const sortedDateKeys = useMemo(
    () =>
      Object.keys(gallery).sort((a, b) => {
        if (a === "unknown-date") return 1;
        if (b === "unknown-date") return -1;
        return b.localeCompare(a);
      }),
    [gallery]
  );

  const dateNavItems = useMemo(
    () =>
      sortedDateKeys
        .filter((dateKey) => dateKey !== "unknown-date")
        .map((dateKey) => {
          const [year, month, day] = dateKey.split("-").map(Number);
          const date = new Date(year, month - 1, day);
          return {
            dateKey,
            label: new Intl.DateTimeFormat("en-US", {
              month: "short",
              day: "numeric",
            }).format(date),
            title: formatDateSubheading(dateKey),
          };
        }),
    [sortedDateKeys]
  );

  const selectedCount = selectedIds.size;
  const selectedSource = selected
    ? resolveAssetUrl(selected.file_path || "", { folder: "gallery" })
    : "";
  const selectedIsImage = String(selected?.file_type || "").toLowerCase() === "image";
  const selectedIsExternalVideo =
    Boolean(selected) && !selectedIsImage && isExternalUrl(String(selected?.file_path || ""));
  const selectedEmbedUrl = selectedIsExternalVideo ? toEmbedUrl(selectedSource) : "";
  const selectedIsDirectVideo =
    !selectedIsImage && (!selectedIsExternalVideo || isDirectVideoFile(selectedSource));
  const selectedIsLinkCard =
    Boolean(selected) &&
    !selectedIsImage &&
    selectedIsExternalVideo &&
    !selectedEmbedUrl &&
    !selectedIsDirectVideo;
  const canPanZoom = selectedIsImage;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectDateGroup = (dateKey) => {
    const ids = (gallery[dateKey] || []).map((item) => item.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      ids.forEach((id) => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectionMode(false);
  };

  const fetchGallery = async () => {
    try {
      const res = await API.get("/admin/gallery");

      // ✅ handle both axios styles safely
      const data = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
        ? res
        : [];

      // 🔒 safety guard
      if (!Array.isArray(data)) {
        console.error("Gallery API returned invalid data:", res);
        setGallery({});
        setFlatGallery([]);
        return [];
      }

      // group by upload date (Google Photos style day sections)
      const grouped = data.reduce((acc, item) => {
        const dateKey = toDateKey(item.uploaded_at);
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(item);
        return acc;
      }, {});

      setFlatGallery(data);
      setGallery(grouped);
      setSelectedIds((prev) => {
        const validIds = new Set(data.map((item) => item.id));
        return new Set([...prev].filter((id) => validIds.has(id)));
      });
      return data;
    } catch (err) {
      console.error("Failed to fetch gallery:", err);
      setGallery({});
      setFlatGallery([]);
      setSelectedIds(new Set());
      return [];
    }
  };

  // 👇 This closes the modal and resets zoom/offset
  const closeModal = () => {
    setSelected(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const openMedia = (id) => {
    const index = flatGallery.findIndex((item) => item.id === id);
    if (index < 0) return;
    setCurrentIndex(index);
    setSelected(flatGallery[index]);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const showNext = () => {
    const next = (currentIndex + 1) % flatGallery.length;
    setCurrentIndex(next);
    setSelected(flatGallery[next]);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const showPrev = () => {
    const prev = (currentIndex - 1 + flatGallery.length) % flatGallery.length;
    setCurrentIndex(prev);
    setSelected(flatGallery[prev]);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  // 👇 Lock scroll when modal is open
  useEffect(() => {
    if (selected) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => (document.body.style.overflow = "auto");
  }, [selected]);

  useEffect(() => {
    fetchGallery();
  }, []);

  useEffect(() => {
    if (!selected) return;

    const handleKey = (e) => {
      if (e.key === "ArrowRight") showNext();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "Escape") closeModal();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, currentIndex, flatGallery]);

  const handleUpload = async (e) => {
    e.preventDefault();

    const input = document.getElementById("galleryFiles");
    const files = Array.from(input.files || []);
    if (!files.length) {
      warningAlert("No files selected", "Please choose files to upload");
      return;
    }
    if (
      uploadCategory === "player_videos" &&
      files.some((file) => !String(file?.type || "").toLowerCase().startsWith("video/"))
    ) {
      warningAlert(
        "Videos only",
        "When uploading to Player Videos, please select only video files."
      );
      return;
    }

    const formData = new FormData();
    for (const file of files) {
      formData.append("files[]", file); // ✅ correct
    }
    formData.append("category", uploadCategory);
    if (String(uploadTitle || "").trim()) {
      formData.append("title", String(uploadTitle).trim());
    }

    setUploading(true);
    const beforeCount = flatGallery.length;
    try {
      const res = await API.post("/admin/gallery/upload", formData);
      await fetchGallery();
      input.value = "";
      setUploadTitle("");

      successAlert(
        "Uploaded!",
        res?.message ||
          (uploadCategory === "player_videos"
            ? "Player videos uploaded successfully"
            : "Files uploaded successfully")
      );
    } catch (err) {
      const isNoResponseError = !err?.status && ["ERR_NETWORK", "NO_RESPONSE"].includes(String(err?.code || ""));
      if (isNoResponseError) {
        const refreshed = await fetchGallery();
        const afterCount = Array.isArray(refreshed) ? refreshed.length : 0;
        if (afterCount > beforeCount) {
          input.value = "";
          setUploadTitle("");
          warningAlert(
            "Upload saved",
            "Files appear uploaded, but response was interrupted. Refresh completed and latest media is shown."
          );
          setUploading(false);
          return;
        }
      }

      errorAlert("Upload failed", err?.message || "Something went wrong");
    }
    setUploading(false);
  };

  const handleAddVideoLink = async (event) => {
    event.preventDefault();

    const videoUrl = String(videoLinkUrl || "").trim();
    const title = String(videoLinkTitle || "").trim();

    if (!videoUrl) {
      warningAlert("Video URL required", "Paste a YouTube or social media video link.");
      return;
    }

    setSavingVideoLink(true);
    try {
      await API.post("/admin/gallery/video-link", {
        title,
        video_url: videoUrl,
        category: "player_videos",
      });
      setVideoLinkTitle("");
      setVideoLinkUrl("");
      await fetchGallery();
      successAlert("Added", "Player video link added successfully.");
    } catch (err) {
      errorAlert("Add failed", err?.message || "Unable to add video link");
    } finally {
      setSavingVideoLink(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmAlert({
      title: "Delete this file?",
      text: "This action cannot be undone",
      confirmText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    try {
      await API.delete(`/admin/gallery/${id}`);
      fetchGallery();
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      successAlert("Deleted", "File removed successfully");
    } catch (err) {
      errorAlert("Delete failed", "Unable to delete the file");
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedCount) {
      warningAlert("No selection", "Select items to delete");
      return;
    }

    const result = await confirmAlert({
      title: `Delete ${selectedCount} file${selectedCount > 1 ? "s" : ""}?`,
      text: "This action cannot be undone",
      confirmText: "Yes, delete all",
    });

    if (!result.isConfirmed) return;

    setBulkDeleting(true);
    try {
      const ids = [...selectedIds];
      const responses = await Promise.allSettled(
        ids.map((id) => API.delete(`/admin/gallery/${id}`))
      );

      const successCount = responses.filter((res) => res.status === "fulfilled").length;
      const failedCount = responses.length - successCount;

      await fetchGallery();

      if (failedCount === 0) {
        successAlert("Deleted", `${successCount} file(s) removed`);
        clearSelection();
      } else {
        errorAlert(
          "Partial delete",
          `${successCount} deleted, ${failedCount} failed. Please retry failed items.`
        );
      }
    } catch (err) {
      errorAlert("Delete failed", "Unable to delete selected files");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkDownload = async () => {
    if (!selectedCount) {
      warningAlert("No selection", "Select items to download");
      return;
    }

    setBulkDownloading(true);
    try {
      const response = await API.post(
        "/admin/gallery/download-bulk",
        { ids: [...selectedIds] },
        { responseType: "blob" }
      );

      const blob = response.data;
      const contentDisposition =
        response.headers?.["content-disposition"] ||
        response.headers?.["Content-Disposition"] ||
        "";

      let filename = "gallery_download.zip";
      const match =
        /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(contentDisposition);
      if (match?.[1]) filename = decodeURIComponent(match[1]);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      errorAlert("Download failed", "Unable to create ZIP for selected files");
    } finally {
      setBulkDownloading(false);
    }
  };

  const handleDownload = (id) => {
    window.open(apiUrl(`/admin/gallery/download/${id}`));
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#f8fafc]">
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Gallery Media</h1>
              <p className="text-sm text-slate-500">
                {selectionMode
                  ? `${selectedCount} selected`
                  : `${flatGallery.length} items`}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {!selectionMode ? (
                <button
                  type="button"
                  onClick={() => setSelectionMode(true)}
                  className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Select
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set(flatGallery.map((item) => item.id)))}
                    className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkDownload}
                    disabled={!selectedCount || bulkDownloading}
                    className="rounded-full bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:cursor-not-allowed disabled:bg-slate-500"
                  >
                    {bulkDownloading
                      ? "Preparing ZIP..."
                      : `Download (${selectedCount})`}
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkDelete}
                    disabled={!selectedCount || bulkDeleting}
                    className="rounded-full bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-400"
                  >
                    {bulkDeleting ? "Deleting..." : `Delete (${selectedCount})`}
                  </button>
                </>
              )}
            </div>
            <div className="flex w-full flex-col gap-2 lg:w-auto lg:min-w-[520px]">
              <form onSubmit={handleUpload} className="flex flex-wrap items-center justify-end gap-2">
                <select
                  value={uploadCategory}
                  onChange={(event) => setUploadCategory(event.target.value)}
                  className="rounded-full border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                >
                  <option value="general">Upload to: Gallery</option>
                  <option value="player_videos">Upload to: Player Videos</option>
                </select>
                <input
                  value={uploadTitle}
                  onChange={(event) => setUploadTitle(event.target.value)}
                  placeholder={
                    uploadCategory === "player_videos"
                      ? "Video text/title (optional)"
                      : "Title text (optional)"
                  }
                  className="min-w-[170px] flex-1 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700"
                />
                <input
                  id="galleryFiles"
                  type="file"
                  multiple
                  accept={uploadCategory === "player_videos" ? "video/*" : "image/*,video/*"}
                  className="hidden"
                />
                <label
                  htmlFor="galleryFiles"
                  className="cursor-pointer rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Select files
                </label>
                <button
                  type="submit"
                  className="rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </button>
              </form>

              <form
                onSubmit={handleAddVideoLink}
                className="flex flex-wrap items-center justify-end gap-2"
              >
                <input
                  value={videoLinkTitle}
                  onChange={(event) => setVideoLinkTitle(event.target.value)}
                  placeholder="Video title (optional)"
                  className="min-w-[170px] flex-1 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700"
                />
                <input
                  value={videoLinkUrl}
                  onChange={(event) => setVideoLinkUrl(event.target.value)}
                  placeholder="YouTube / social video link"
                  className="min-w-[220px] flex-[2] rounded-full border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700"
                />
                <button
                  type="submit"
                  disabled={savingVideoLink}
                  className="rounded-full bg-fuchsia-600 px-4 py-2 text-sm font-medium text-white hover:bg-fuchsia-700 disabled:cursor-not-allowed disabled:bg-fuchsia-400"
                >
                  {savingVideoLink ? "Adding..." : "Add Player Video Link"}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-7xl gap-4 px-4 pb-10 pt-4 sm:px-6">
          <div className="min-w-0 flex-1">
            {sortedDateKeys.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">
                No media found. Upload files or add player video links to start.
              </div>
            )}

            {sortedDateKeys.map((dateKey) => (
              <section
                key={dateKey}
                ref={(el) => {
                  sectionRefs.current[dateKey] = el;
                }}
                className="mb-8"
              >
                <div className="sticky top-[64px] z-20 mb-3 flex items-end justify-between bg-[#f8fafc]/95 py-2 backdrop-blur">
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">
                      {formatDateHeading(dateKey)}
                    </h2>
                    <p className="text-xs text-slate-500">
                      {formatDateSubheading(dateKey)}
                    </p>
                  </div>
                  <span className="text-xs font-medium text-slate-500">
                    {gallery[dateKey].length}
                  </span>
                </div>
                {selectionMode && (
                  <div className="mb-3">
                    <button
                      type="button"
                      onClick={() => selectDateGroup(dateKey)}
                      className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Select date group
                    </button>
                  </div>
                )}

                <div className="columns-2 gap-3 sm:columns-3 lg:columns-4 xl:columns-5">
                  {gallery[dateKey].map((file) => {
                    const fileType = String(file.file_type || "").toLowerCase();
                    const isImage = fileType === "image";
                    const mediaUrl = resolveAssetUrl(file.file_path, { folder: "gallery" });
                    const isExternalVideo = !isImage && isExternalUrl(file.file_path);
                    const canDownload = !isExternalVideo;
                    const embedUrl = isExternalVideo ? toEmbedUrl(mediaUrl) : "";
                    const isLinkCard = isExternalVideo && !embedUrl && !isDirectVideoFile(mediaUrl);
                    const badgeText = categoryLabel(file.category, fileType);
                    const badgeClass = categoryBadgeClass(file.category, fileType);
                    const badgeShift = selectionMode ? "left-10" : "left-2";

                    const handleOpen = () => {
                      if (selectionMode) {
                        toggleSelect(file.id);
                        return;
                      }
                      if (isLinkCard) {
                        window.open(mediaUrl, "_blank", "noopener,noreferrer");
                        return;
                      }
                      openMedia(file.id);
                    };

                    return (
                      <div
                        key={file.id}
                        className={`group relative mb-3 break-inside-avoid overflow-hidden rounded-xl bg-slate-200 shadow-sm ${
                          selectedIds.has(file.id) ? "ring-2 ring-blue-500" : ""
                        }`}
                      >
                        {selectionMode && (
                          <label className="absolute left-2 top-2 z-20 cursor-pointer rounded-full bg-white/90 p-1 shadow">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(file.id)}
                              onChange={() => toggleSelect(file.id)}
                              className="h-4 w-4"
                            />
                          </label>
                        )}

                        <span
                          className={`absolute ${badgeShift} top-2 z-20 rounded-full px-2 py-0.5 text-[10px] font-semibold ${badgeClass}`}
                        >
                          {badgeText}
                        </span>

                        {isImage ? (
                          <img
                            src={mediaUrl}
                            alt={file.title}
                            onClick={handleOpen}
                            className="h-auto w-full cursor-pointer object-cover transition duration-300 group-hover:scale-[1.02]"
                          />
                        ) : isExternalVideo ? (
                          <button
                            type="button"
                            onClick={handleOpen}
                            className="flex aspect-video w-full flex-col items-center justify-center gap-1 bg-slate-900/90 px-3 py-6 text-center text-white"
                          >
                            <span className="text-2xl">▶</span>
                            <span className="text-xs font-semibold">
                              {embedUrl ? "Embedded video link" : "External video link"}
                            </span>
                            <span className="text-[11px] text-slate-300">{hostLabel(mediaUrl)}</span>
                          </button>
                        ) : (
                          <video
                            src={mediaUrl}
                            onClick={handleOpen}
                            className="h-auto w-full cursor-pointer object-cover"
                          />
                        )}

                        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 transition group-hover:opacity-100">
                          <p className="truncate text-xs text-white">{file.title}</p>
                        </div>

                        <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition group-hover:opacity-100">
                          {canDownload ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(file.id);
                              }}
                              className="rounded-full bg-white/90 px-2 py-1 text-xs text-slate-700 shadow hover:bg-white"
                            >
                              Download
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(mediaUrl, "_blank", "noopener,noreferrer");
                              }}
                              className="rounded-full bg-white/90 px-2 py-1 text-xs text-slate-700 shadow hover:bg-white"
                            >
                              Open link
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(file.id);
                            }}
                            className="rounded-full bg-red-600/90 px-2 py-1 text-xs text-white shadow hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          {dateNavItems.length > 0 && (
            <aside className="sticky top-24 hidden h-fit w-24 rounded-xl border border-slate-200 bg-white p-2 shadow-sm lg:block">
              <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Jump
              </p>
              <div className="space-y-1">
                {dateNavItems.map((item) => (
                  <button
                    key={item.dateKey}
                    title={item.title}
                    onClick={() =>
                      sectionRefs.current[item.dateKey]?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      })
                    }
                    className="w-full rounded-md px-2 py-1 text-left text-xs text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </aside>
          )}
        </div>

        {/* Modal */}
        {selected && (
          <div
            className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 overflow-hidden"
            onClick={(e) => e.target === e.currentTarget && closeModal()}
          >
            {/* Close + Zoom Controls */}
            <div className="absolute top-5 right-6 flex gap-3 z-50">
              <button
                onClick={closeModal}
                className="text-white text-2xl font-bold hover:text-red-500"
              >
                ✕
              </button>
            </div>

            {/* Zoom Controls */}
            {canPanZoom ? (
              <div className="absolute top-5 left-6 flex gap-3 z-50">
                <button
                  onClick={() => setZoom((z) => Math.min(z + 0.25, 5))}
                  className="bg-white/30 text-white px-3 py-1 rounded hover:bg-white/50"
                >
                  ➕
                </button>
                <button
                  onClick={() => setZoom((z) => Math.max(z - 0.25, 1))}
                  className="bg-white/30 text-white px-3 py-1 rounded hover:bg-white/50"
                >
                  ➖
                </button>
                <button
                  onClick={() => {
                    setZoom(1);
                    setOffset({ x: 0, y: 0 });
                  }}
                  className="bg-white/30 text-white px-3 py-1 rounded hover:bg-white/50"
                >
                  🔄
                </button>
              </div>
            ) : null}
                {flatGallery.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      showPrev();
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white text-3xl px-4 py-2 rounded-full hover:bg-black/80 z-50"
                  >
                    ‹
                  </button>
                )}
            {/* Image / Video View */}
            <div
              className={`max-w-[95vw] max-h-[95vh] overflow-hidden flex items-center justify-center relative ${
                canPanZoom ? "cursor-grab active:cursor-grabbing" : ""
              }`}
              onWheel={(e) => {
                if (!canPanZoom) return;
                e.preventDefault();
                if (e.deltaY < 0) setZoom((z) => Math.min(z + 0.2, 5));
                else setZoom((z) => Math.max(z - 0.2, 1));
              }}
              onMouseDown={(e) => {
                if (!canPanZoom) return;
                e.preventDefault();
                setDragging(true);
                setStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
              }}
              onMouseMove={(e) => {
                if (!canPanZoom) return;
                if (!dragging) return;
                setOffset({
                  x: e.clientX - start.x,
                  y: e.clientY - start.y,
                });
              }}
              onMouseUp={() => setDragging(false)}
              onMouseLeave={() => setDragging(false)}
              onTouchStart={(e) => {
                if (!canPanZoom) return;
                if (e.touches.length === 1) {
                  const t = e.touches[0];
                  setDragging(true);
                  setStart({ x: t.clientX - offset.x, y: t.clientY - offset.y });
                }
              }}
              onTouchMove={(e) => {
                if (!canPanZoom) return;
                if (!dragging || e.touches.length !== 1) return;
                const t = e.touches[0];
                setOffset({ x: t.clientX - start.x, y: t.clientY - start.y });
              }}
              onTouchEnd={() => setDragging(false)}
            >
              {selectedIsImage ? (
                <img
                  src={selectedSource}
                  alt="Preview"
                  className="rounded object-contain transition-transform duration-300 ease-in-out select-none"
                  style={{
                    maxHeight: "95vh",
                    maxWidth: "95vw",
                    transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                    transformOrigin: "center center",
                  }}
                  draggable="false"
                  onDoubleClick={() => {
                    setZoom(1);
                    setOffset({ x: 0, y: 0 });
                  }}
                />
              ) : selectedEmbedUrl ? (
                <iframe
                  src={selectedEmbedUrl}
                  title={selected.title || "Player video"}
                  className="h-[75vh] w-[90vw] max-w-5xl rounded"
                  allow="autoplay; encrypted-media; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : selectedIsLinkCard ? (
                <div className="w-[92vw] max-w-2xl rounded-2xl bg-slate-900 p-8 text-center text-white">
                  <p className="text-sm uppercase tracking-wide text-slate-300">External Video Link</p>
                  <h3 className="mt-2 text-xl font-semibold">
                    {selected.title || hostLabel(selectedSource)}
                  </h3>
                  <p className="mt-2 text-sm text-slate-300 break-all">{selectedSource}</p>
                  <a
                    href={selectedSource}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-5 inline-block rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    Open on platform
                  </a>
                </div>
              ) : (
                <video
                  src={selectedSource}
                  controls
                  autoPlay
                  className="rounded object-contain max-h-[90vh] max-w-[90vw]"
                />
              )}
            </div>
            {flatGallery.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  showNext();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white text-3xl px-4 py-2 rounded-full hover:bg-black/80 z-50"
              >
                ›
              </button>
            )}
          </div>
        )}

      </div>
      <Footer />
    </>
  );
};

export default ManageGallery;
